import React from 'react';
import FieldReportHeader from '../field-report/FieldReportHeader';
import { Wrench, Truck, Check, Clock, CheckCircle, XCircle, MapPin } from 'lucide-react';
import { resolveSignatureUrl, resolveFileUrl } from '../../lib/fileUtils';
import { formatDateET, formatDateTimeET } from '../../lib/dateHelpers';
import { SafeImage } from '../SafeImage';

// -----------------------------------------------------------------------
// TemplateAnswersSection
//
// Renders the Feb-2026 checklist_values dict against the resolved
// template (Grease Form / Maintenance Request Form / Confined Space
// Evaluation). Structured for print — each template section becomes
// its own labelled block, each repeatable section becomes a small
// table, and inline `signature` items render as an <img>. Kept in the
// same file as MaintenanceDocument since it's only ever consumed here.
// -----------------------------------------------------------------------
function isRepeatableSectionName(name) {
  return typeof name === 'string' && /\brepeatable\b/i.test(name);
}

function fmtAnswer(cellDef, cellV) {
  if (!cellV) return '—';
  if (cellDef?.type === 'yesno') return cellV.value ? String(cellV.value) : '—';
  if (cellDef?.type === 'check') return cellV.checked ? '✓' : '○';
  if (cellDef?.type === 'multiselect') {
    return Array.isArray(cellV.selections) && cellV.selections.length ? cellV.selections.join(', ') : '—';
  }
  return (cellV.value != null && cellV.value !== '') ? String(cellV.value) : '—';
}

function TemplateAnswersSection({ template, values }) {
  const sections = template?.sections || [];
  let flatIdx = 0;
  const nodes = [];
  sections.forEach((sec, sIdx) => {
    const sectionName = sec.name || 'Section';
    const items = sec.items || [];
    if (isRepeatableSectionName(sectionName)) {
      const v = values[String(flatIdx)] || {};
      const rows = v.rows || [];
      nodes.push(
        <div key={`sec-${sIdx}`} className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{sectionName}</p>
          {rows.length === 0 || items.length === 0 ? (
            <p className="text-[11px] italic text-slate-500">No entries.</p>
          ) : (
            <table className="w-full text-[10px] border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  {items.map((it, i) => (
                    <th key={i} className="border border-slate-200 px-2 py-1 text-left">
                      {it.label}{it.unit ? ` (${it.unit})` : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {items.map((cellDef, cIdx) => (
                      <td key={cIdx} className="border border-slate-200 px-2 py-1">
                        {fmtAnswer(cellDef, row[cIdx])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>,
      );
      flatIdx += 1;
      return;
    }
    if (items.length === 0) {
      nodes.push(
        <div key={`sec-${sIdx}`} className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{sectionName}</p>
          <p className="text-[11px] italic text-slate-500">(no items in this section)</p>
        </div>,
      );
      return;
    }
    nodes.push(
      <div key={`sec-${sIdx}`} className="mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{sectionName}</p>
        <div className="space-y-1.5">
          {items.map((it, iIdx) => {
            const v = values[String(flatIdx)] || {};
            flatIdx += 1;
            if (it.type === 'signature') {
              const sig = v.signature_url || v.value;
              return (
                <div key={iIdx} className="bg-white border border-slate-200 rounded p-2">
                  <p className="text-[11px] font-medium text-slate-700 mb-1">{it.label}</p>
                  {sig
                    ? <img src={sig} alt="signature" className="h-12 border border-slate-200 bg-white" />
                    : <span className="text-[10px] italic text-slate-500">Not signed</span>}
                </div>
              );
            }
            return (
              <div key={iIdx} className="bg-white border border-slate-200 rounded p-2 flex items-start gap-3">
                <span className="text-[11px] font-medium text-slate-700 flex-1">{it.label}{it.unit ? ` (${it.unit})` : ''}</span>
                <span className="text-[11px] text-slate-900 flex-1 text-right">{fmtAnswer(it, v)}</span>
              </div>
            );
          })}
        </div>
      </div>,
    );
  });
  return <>{nodes}</>;
}

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'approved': return 'bg-green-100 text-green-700';
    case 'submitted_pending_review':
    case 'pending': return 'bg-blue-100 text-blue-700';
    case 'rejected': return 'bg-red-100 text-red-700';
    case 'draft': return 'bg-slate-100 text-slate-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'approved': return 'Approved';
    case 'submitted_pending_review':
    case 'pending': return 'Pending Review';
    case 'rejected': return 'Rejected';
    case 'draft': return 'Draft';
    default: return status || 'Draft';
  }
};

const formatDate = (dateStr) => formatDateET(dateStr);

const formatDateTime = (dateStr) => formatDateTimeET(dateStr);

// Get form type display name
const getFormTypeTitle = (formType) => {
  switch (formType) {
    case 'Maintenance': return 'Maintenance Report';
    case 'Grease': return 'Grease Report';
    case 'Equipment Check In': return 'Equipment Check-In Report';
    case 'Equipment Check Out': return 'Equipment Check-Out Report';
    case 'Confined Space Evaluation': return 'Confined Space Evaluation Report';
    default: return formType || 'Maintenance Report';
  }
};

// Get report type for header
const getReportType = (formType) => {
  switch (formType) {
    case 'Maintenance': return 'Maintenance';
    case 'Grease': return 'Grease Form';
    case 'Equipment Check In': return 'Check-In';
    case 'Equipment Check Out': return 'Check-Out';
    case 'Confined Space Evaluation': return 'Confined Space Evaluation';
    default: return 'Maintenance';
  }
};

export default function MaintenanceDocument({ form, printRef, approvalCapture }) {
  // Handle checklist - support both 'checked' boolean and 'status' string
  const getChecklistStatus = (item) => {
    const isChecked = item.status === 'pass' || item.checked === true;
    const isFailed = item.status === 'fail';
    return { isChecked, isFailed };
  };

  return (
    <div 
      ref={printRef} 
      id="maintenance-document" 
      className="bg-white w-full max-w-3xl shadow-xl rounded-lg overflow-hidden print:shadow-none print:rounded-none print:max-w-none" 
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <FieldReportHeader 
        reportType={getReportType(form.form_type)} 
        reportNumber={form.form_number || form.id?.slice(0, 8)} 
      />
      
      <div className="px-6 py-4">
        {/* Title & Status */}
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-slate-900">{getFormTypeTitle(form.form_type)}</h2>
          <span data-testid="doc-status-badge" data-status={form.status || ''} className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(form.status)}`}>
            {getStatusLabel(form.status)}
          </span>
        </div>

        {/* Job Information — Round 6/7: Grease Form, Maintenance
            Request Form, and Confined Space Evaluation can all carry
            job context now. Matches the "Job Information" box style
            used by PreCon/Soft Dig/Locate exactly (was previously
            missing here entirely — this is what made CSE's rendered
            PDF look visually inconsistent with the other 3 field
            report types). Hidden when the form has no job attached
            (plain equipment Check-In/Check-Out transactions). */}
        {(form.job_number || form.job_service_address) && (
          <div className="mb-4 p-3 bg-slate-50 rounded">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Job Information</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-[10px]">Job Number</p>
                <p className="font-medium text-amber-600">{form.job_number || '—'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px]">Job Date</p>
                <p className="font-medium text-slate-700">{formatDate(form.job_date)}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px]">Assigned Team</p>
                <p className="font-medium text-slate-700 flex items-center gap-1">
                  {Array.isArray(form.job_assigned_team) ? form.job_assigned_team.join(', ') : (form.job_assigned_team || '—')}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-3">
              <div>
                <p className="text-slate-500 text-[10px]">Service Address</p>
                <p className="font-medium text-slate-700 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  {form.job_service_address || '—'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Equipment Information */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Equipment Information</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Equipment Name</p>
              <p className="font-medium text-amber-600 flex items-center gap-1">
                <Truck className="w-3 h-3" />
                {form.equipment_name || '—'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Equipment ID</p>
              <p className="font-medium text-slate-700">{form.equipment_id || form.other_equipment || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Date</p>
              <p className="font-medium text-slate-700">{formatDate(form.date)}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div>
              <p className="text-slate-500 text-[10px]">Performed By</p>
              <p className="font-medium text-slate-700">{form.performed_by || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Mileage</p>
              <p className="font-medium text-slate-700">{form.mileage || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Condition</p>
              <p className={`font-medium ${
                form.condition === 'Good' ? 'text-green-600' : 
                form.condition === 'Fair' ? 'text-amber-600' : 
                form.condition === 'Poor' ? 'text-red-600' : 'text-slate-700'
              }`}>{form.condition || '—'}</p>
            </div>
          </div>
        </div>

        {/* Inspection Checklist */}
        {form.checklist && form.checklist.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Inspection Checklist</p>
            <div className="space-y-2">
              {form.checklist.map((item, idx) => {
                const { isChecked, isFailed } = getChecklistStatus(item);
                const hasPhotos = item.photos && item.photos.length > 0;
                return (
                  <div key={idx} className={`bg-white border rounded p-2 ${isFailed ? 'border-red-200 bg-red-50' : 'border-slate-200'}`} data-testid={`doc-checklist-item-${idx}`} data-answer-state={isFailed ? 'failed' : isChecked ? 'checked' : 'pending'}>
                    <div className="flex items-center gap-2">
                      {isChecked ? (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : isFailed ? (
                        <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-slate-200 flex-shrink-0" />
                      )}
                      <span className="text-slate-700 text-[11px] font-medium flex-1" data-testid={`doc-checklist-label-${idx}`}>{item.item || item.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        isChecked ? 'bg-green-100 text-green-700' :
                        isFailed ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {isChecked ? 'Done' : isFailed ? 'Failed' : 'Pending'}
                      </span>
                    </div>
                    {item.notes && (
                      <p className="text-slate-500 text-[10px] mt-1 ml-6">{item.notes}</p>
                    )}
                    {/* Checklist Item Photos — 2026-09-30: enlarged from a
                        64px thumbnail row to a verification-size grid (user
                        report: too small to confirm gauge readings/condition
                        on equipment check-in/out forms). Matches the ~3in-wide
                        sizing field reports already use (LocateDocument.js). */}
                    {hasPhotos && (
                      <div className="grid grid-cols-2 gap-2 mt-2 ml-6">
                        {item.photos.slice(0, 4).map((photoUrl, photoIdx) => (
                          <SafeImage
                            key={photoIdx}
                            src={resolveFileUrl(photoUrl, 'preview')}
                            alt={`${item.item || item.name} - Photo ${photoIdx + 1}`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-56 object-cover rounded border border-slate-200"
                          />
                        ))}
                        {item.photos.length > 4 && (
                          <span className="text-[10px] text-slate-500 self-center">+{item.photos.length - 4} more</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Template-backed checklist (Feb 2026 — Grease / Maintenance
            Request Form / Confined Space Evaluation). Renders each
            section of the resolved template against the stored
            checklist_values dict. `form.template` is attached to the
            form doc client-side after fetch — see MaintenancePreview. */}
        {form.template && form.checklist_values && (
          <TemplateAnswersSection
            template={form.template}
            values={form.checklist_values}
          />
        )}

        {/* Photos Section — 2026-09-30: same verification-size bump as the
            per-item checklist photos above. */}
        {form.photos && form.photos.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Photos</p>
            <div className="grid grid-cols-2 gap-2">
              {form.photos.slice(0, 8).map((photo, idx) => (
                <div key={idx}>
                  <SafeImage
                    src={resolveFileUrl(photo.url || photo, 'preview')} 
                    alt={`Photo ${idx + 1}`} 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-56 object-cover rounded border" 
                  />
                  {photo.caption && <p className="text-[9px] text-slate-500 mt-1 truncate">{photo.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {form.notes && (
          <div className="mb-4 p-3 bg-slate-50 rounded">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Additional Notes</p>
            <p className="text-sm text-slate-600">{form.notes}</p>
          </div>
        )}

        {/* Signatures Section */}
        <div className="mb-4 border-t border-slate-200 pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Signatures & Authorization</p>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Submitter Signature */}
            <div className="p-3 bg-slate-50 rounded">
              <p className="text-[10px] font-semibold text-slate-500 mb-2">Performed By / Submitted By</p>
              {form.submitter_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(form, 'submitter_signature')} 
                    alt="Submitter Signature" 
                    className="max-h-16 mb-2 border border-slate-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-slate-700">{form.submitter_name || form.performed_by || '—'}</p>
                  <p className="text-[10px] text-slate-500">{formatDateTime(form.submitter_signed_at || form.created_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-400 text-sm pb-1">{form.performed_by || 'Not signed'}</span>
                  </div>
                  <p className="text-[10px] text-slate-500">Technician Signature & Date</p>
                </div>
              )}
            </div>

            {/* Reviewer/Approver Signature */}
            <div className={`p-3 rounded ${
              form.status === 'completed' || form.status === 'approved' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-slate-50'
            }`}>
              <p className={`text-[10px] font-semibold mb-2 ${
                form.status === 'completed' || form.status === 'approved' 
                  ? 'text-green-600' 
                  : 'text-slate-500'
              }`}>
                Manager Approval
              </p>
              {form.reviewer_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(form, 'reviewer_signature')} 
                    alt="Reviewer Signature" 
                    className="max-h-16 mb-2 border border-green-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-green-700">{form.reviewer_name || form.reviewed_by || '—'}</p>
                  <p className="text-[10px] text-green-600">{formatDateTime(form.reviewer_signed_at || form.reviewed_at)}</p>
                </div>
              ) : approvalCapture?.canApprove ? (
                // Inline signature capture — matches PreCon's "sign directly
                // in the document" pattern instead of a separate popup modal.
                // The real signature drawn here is what gets POSTed to
                // /maintenance/{id}/approve and saved as reviewer_signature_url.
                <div data-testid="cse-inline-approval-box">
                  <p className="text-[10px] text-slate-500 mb-1">Sign below to approve this report</p>
                  <canvas
                    ref={approvalCapture.sigCanvasRef}
                    width={300}
                    height={100}
                    className="w-full border border-dashed border-slate-300 rounded cursor-crosshair touch-none bg-white mb-1"
                    onMouseDown={approvalCapture.onStartDrawing}
                    onMouseMove={approvalCapture.onDraw}
                    onMouseUp={approvalCapture.onStopDrawing}
                    onMouseLeave={approvalCapture.onStopDrawing}
                    onTouchStart={approvalCapture.onStartDrawing}
                    onTouchMove={approvalCapture.onDraw}
                    onTouchEnd={approvalCapture.onStopDrawing}
                    data-testid="cse-inline-approval-signature"
                  />
                  <button
                    type="button"
                    onClick={approvalCapture.onClear}
                    className="no-print text-[10px] text-slate-500 underline"
                    data-testid="cse-inline-approval-clear"
                  >
                    Clear Signature
                  </button>
                  <p className="text-[10px] text-slate-500 mt-1">Manager Signature & Date</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">
                      {form.status === 'submitted_pending_review' || form.status === 'pending' 
                        ? 'Awaiting approval' 
                        : 'Not signed'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Manager Signature & Date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Form Status</p>
          <div className="flex flex-wrap gap-4 text-[11px]">
            {form.created_at && (
              <div className="flex items-center gap-1 text-slate-600">
                <Clock className="w-3 h-3" />
                <span>Created: {formatDateTime(form.created_at)}</span>
              </div>
            )}
            {form.submitted_at && (
              <div className="flex items-center gap-1 text-blue-600">
                <CheckCircle className="w-3 h-3" />
                <span>Submitted: {formatDateTime(form.submitted_at)}</span>
              </div>
            )}
            {form.reviewed_at && (form.status === 'completed' || form.status === 'approved') && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-3 h-3" />
                <span>Approved: {formatDateTime(form.reviewed_at)}</span>
              </div>
            )}
            {form.rejected_at && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="w-3 h-3" />
                <span>Rejected: {formatDateTime(form.rejected_at)}</span>
              </div>
            )}
          </div>
          {form.rejection_reason && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
              <span className="font-semibold">Rejection Reason:</span> {form.rejection_reason}
            </div>
          )}
        </div>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="px-6 py-3 bg-slate-100 border-t border-slate-200">
        <p className="text-[9px] text-slate-500 text-center italic">
          This form documents equipment maintenance and inspection at the time of service. 
          All checklist items should be verified before equipment operation.
        </p>
      </div>
      
      {/* Footer bar */}
      <div className="px-6 py-3 bg-slate-800 flex items-center justify-between text-[10px] text-slate-300">
        <span>Center Line Locating, LLC · Charlotte, NC · (704) 315-5820</span>
        <span>{getFormTypeTitle(form.form_type)} #{form.form_number || form.id?.slice(0, 8)}</span>
      </div>
    </div>
  );
}
